import { SetterOrUpdater } from "recoil";
import {
  setCookieAccessToken,
  removeCookieAccessToken,
  getBaseUrl,
} from "../../../utils/requestUtils";
import { GetNewProviderInfoFromToken } from "../../Login/Services/LoginService";
import {
  UserAuthProviderResponse,
  UserCheckEmailResponse,
  UserDefaultRequisitionsResponse,
} from "../../../recoil/types";
import { RefreshRequisitionRows } from "../../Grid/Services/GridService";
import { fetchOrLogout } from "../../../utils/helpers";

// const CheckMessages  = {
//   200 JWT is OK and no account with body {email: str}
//   201 Account exists regardless of JWT state with body {email: str}
// Failed Response:
//   400 Expired JWT and no account with body {email: str}
//   401 Invalid JWT with no body
// }

export const auth0ResponseMessages: { [id: number]: string } = {
  201: "",
  400: "Unknown error during registration.",
  404: "Unknown signup link. Please contact customer support if you wish to continue.",
  406: "The signup link has expired. Please contact customer support if you wish to continue.",
  409: "Invalid URL. Please contact customer support if you wish to continue.",
  412: "Please verify your personal details and try again. If the problem persists, please contact customer support.",
  500: "Unknown error during registration.",
  501: "Network error - please check connection.",
};

export interface CreateAccoutRequestType {
  password: string;
  token: string;
}

export const CreateAccountRequest = async (
  accountInfo: CreateAccoutRequestType,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<[number, UserAuthProviderResponse | undefined]> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/user_sign_up`,
      {
        body: JSON.stringify(accountInfo),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
      setLoggedOut,
    );
    if (response.ok) {
      return [
        response.status,
        (await response.json()) as UserAuthProviderResponse,
      ];
    }
    return [response.status, undefined];
  } catch (e) {
    return [501, undefined];
  }
};

export const CheckCreateAccountToken = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<[number, UserCheckEmailResponse | undefined]> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/check_sign_up`,
      {
        body: JSON.stringify({ token }),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
      setLoggedOut,
    );
    if (response.ok) {
      return [
        response.status,
        (await response.json()) as UserCheckEmailResponse,
      ];
    }
    return [response.status, undefined];
  } catch (e) {
    return [501, undefined];
  }
};

export const UpdateEmailOnlyRequest = async (
  email: string,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response | undefined> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/account/email`,
      {
        body: JSON.stringify({ new_email: email }),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );
    return response;
  } catch (e) {
    return undefined;
  }
};

export const RefreshProviderInfo = async (
  token: string,
  providerSetter: SetterOrUpdater<UserAuthProviderResponse | undefined>,
  requisitionSetter: SetterOrUpdater<
    UserDefaultRequisitionsResponse | undefined
  >,
  requestCompleteSetter: SetterOrUpdater<boolean>,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<void> => {
  try {
    const response = await GetNewProviderInfoFromToken(token, setLoggedOut);
    let newProviderInfo: UserAuthProviderResponse | undefined;
    if (response.ok) {
      newProviderInfo = await response.json();
    }
    providerSetter(newProviderInfo);
    if (newProviderInfo) {
      setCookieAccessToken(newProviderInfo.token);
      await RefreshRequisitionRows(
        newProviderInfo.token as string,
        requisitionSetter,
        setLoggedOut,
        undefined,
        requestCompleteSetter,
      );
    } else {
      removeCookieAccessToken();
    }
  } catch (e) {
    return undefined;
  }
};
